/**
 * TwoFactorSetup - Component for setting up 2FA
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, 
  Copy, 
  Check, 
  AlertTriangle, 
  Key,
  RefreshCw,
  Eye,
  EyeOff,
  Smartphone,
} from 'lucide-react';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';

interface TwoFactorStatus {
  enabled: boolean;
  backupCodesRemaining: number;
}

interface SetupResponse {
  secret: string;
  qrCode: string;
  backupCodes: string[];
}

export default function TwoFactorSetup() {
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState<SetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  const queryClient = useQueryClient();

  // Get 2FA status
  const { data: status, isLoading } = useQuery<TwoFactorStatus>({
    queryKey: ['2fa-status'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/2fa/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      return data.data;
    },
  });

  // Setup mutation
  const setupMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/2fa/setup', {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      return data.data as SetupResponse;
    },
    onSuccess: (data) => {
      setSetupData(data);
      setShowSetup(true);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to setup 2FA');
    },
  });

  // Enable mutation
  const enableMutation = useMutation({
    mutationFn: async (code: string) => {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/2fa/enable', {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    onSuccess: () => {
      toast.success('2FA enabled successfully!');
      setShowSetup(false);
      setSetupData(null);
      setVerificationCode('');
      queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Invalid verification code');
    },
  });

  // Disable mutation
  const disableMutation = useMutation({
    mutationFn: async (code: string) => {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/2fa/disable', {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    onSuccess: () => {
      toast.success('2FA disabled');
      setShowDisableForm(false);
      setDisableCode('');
      queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Invalid verification code');
    },
  });

  const handleCopySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
      toast.success('Secret copied to clipboard');
    }
  };

  const handleCopyBackupCodes = () => {
    if (setupData?.backupCodes) {
      navigator.clipboard.writeText(setupData.backupCodes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-32 bg-white/[0.04] rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-[#161b22] border border-white/[0.08] rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn(
              "h-12 w-12 rounded-xl flex items-center justify-center",
              status?.enabled ? "bg-[#10b981]/20" : "bg-[#f59e0b]/20"
            )}>
              <Shield className={cn(
                "h-6 w-6",
                status?.enabled ? "text-[#10b981]" : "text-[#f59e0b]"
              )} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Two-Factor Authentication
              </h3>
              <p className={cn(
                "text-sm",
                status?.enabled ? "text-[#10b981]" : "text-[#f59e0b]"
              )}>
                {status?.enabled 
                  ? `Enabled • ${status.backupCodesRemaining} backup codes remaining`
                  : 'Not enabled'}
              </p>
            </div>
          </div>

          {status?.enabled ? (
            <button
              onClick={() => setShowDisableForm(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-[#f6465d]/10 text-[#f6465d] hover:bg-[#f6465d]/20 transition-colors"
            >
              Disable 2FA
            </button>
          ) : (
            <button
              onClick={() => setupMutation.mutate()}
              disabled={setupMutation.isPending}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                "bg-[#00d4aa] text-black hover:bg-[#00d4aa]/90",
                setupMutation.isPending && "opacity-50 cursor-wait"
              )}
            >
              {setupMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                'Enable 2FA'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Disable Form */}
      {showDisableForm && (
        <div className="bg-[#161b22] border border-[#f6465d]/30 rounded-xl p-6">
          <div className="flex items-start gap-4 mb-4">
            <AlertTriangle className="h-6 w-6 text-[#f6465d] flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-white mb-1">Disable Two-Factor Authentication</h4>
              <p className="text-sm text-[#8b949e]">
                This will remove 2FA protection from your account. Enter your current 2FA code to confirm.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="flex-1 px-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-center text-xl tracking-widest font-mono focus:outline-none focus:border-[#f6465d]/50"
              maxLength={6}
            />
            <button
              onClick={() => disableMutation.mutate(disableCode)}
              disabled={disableCode.length !== 6 || disableMutation.isPending}
              className="px-4 py-2 rounded-lg bg-[#f6465d] text-white font-medium hover:bg-[#f6465d]/90 disabled:opacity-50 transition-colors"
            >
              {disableMutation.isPending ? 'Disabling...' : 'Disable'}
            </button>
            <button
              onClick={() => {
                setShowDisableForm(false);
                setDisableCode('');
              }}
              className="px-4 py-2 rounded-lg bg-white/[0.04] text-white hover:bg-white/[0.08] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Setup Flow */}
      {showSetup && setupData && (
        <div className="bg-[#161b22] border border-white/[0.08] rounded-xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/[0.08] bg-gradient-to-r from-[#00d4aa]/10 to-transparent">
            <h4 className="font-semibold text-white">Setup Two-Factor Authentication</h4>
            <p className="text-sm text-[#8b949e]">Follow these steps to secure your account</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Step 1: QR Code */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-full bg-[#00d4aa] text-black text-xs font-bold flex items-center justify-center">
                  1
                </div>
                <h5 className="font-medium text-white">Scan QR Code</h5>
              </div>
              
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="bg-white p-3 rounded-lg w-fit">
                    <img 
                      src={setupData.qrCode} 
                      alt="2FA QR Code" 
                      className="h-[180px] w-[180px]"
                    />
                  </div>
                </div>
                
                <div className="flex-1">
                  <p className="text-sm text-[#8b949e] mb-3">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  
                  <div className="bg-white/[0.02] border border-white/[0.08] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Key className="h-4 w-4 text-[#8b949e]" />
                      <span className="text-xs text-[#8b949e]">Manual entry code</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono text-white break-all">
                        {setupData.secret}
                      </code>
                      <button
                        onClick={handleCopySecret}
                        className="p-1.5 rounded hover:bg-white/[0.08] transition-colors"
                      >
                        {copiedSecret ? (
                          <Check className="h-4 w-4 text-[#10b981]" />
                        ) : (
                          <Copy className="h-4 w-4 text-[#8b949e]" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Backup Codes */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-full bg-[#00d4aa] text-black text-xs font-bold flex items-center justify-center">
                  2
                </div>
                <h5 className="font-medium text-white">Save Backup Codes</h5>
              </div>
              
              <div className="bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#f59e0b]">
                    Save these backup codes in a safe place. You can use them to access your account if you lose your phone.
                  </p>
                </div>
              </div>

              <div className="bg-white/[0.02] border border-white/[0.08] rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setShowBackupCodes(!showBackupCodes)}
                    className="flex items-center gap-2 text-sm text-[#8b949e] hover:text-white transition-colors"
                  >
                    {showBackupCodes ? (
                      <>
                        <EyeOff className="h-4 w-4" />
                        Hide codes
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Show codes
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCopyBackupCodes}
                    className="flex items-center gap-2 text-sm text-[#00d4aa] hover:text-[#00d4aa]/80 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                    Copy all
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {setupData.backupCodes.map((code, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 bg-white/[0.04] rounded text-center font-mono text-sm"
                    >
                      {showBackupCodes ? code : '••••••••••'}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 3: Verify */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-full bg-[#00d4aa] text-black text-xs font-bold flex items-center justify-center">
                  3
                </div>
                <h5 className="font-medium text-white">Verify Setup</h5>
              </div>
              
              <p className="text-sm text-[#8b949e] mb-4">
                Enter the 6-digit code from your authenticator app to complete setup.
              </p>
              
              <div className="flex gap-3">
                <div className="relative flex-1 max-w-[200px]">
                  <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8b949e]" />
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-center text-xl tracking-widest font-mono focus:outline-none focus:border-[#00d4aa]/50"
                    maxLength={6}
                  />
                </div>
                <button
                  onClick={() => enableMutation.mutate(verificationCode)}
                  disabled={verificationCode.length !== 6 || enableMutation.isPending}
                  className={cn(
                    "px-6 py-3 rounded-lg font-medium transition-colors",
                    "bg-[#00d4aa] text-black hover:bg-[#00d4aa]/90",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {enableMutation.isPending ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    'Enable 2FA'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      {!status?.enabled && !showSetup && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
          <h4 className="font-semibold text-white mb-3">Why enable 2FA?</h4>
          <ul className="space-y-3 text-sm text-[#8b949e]">
            <li className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#00d4aa] flex-shrink-0 mt-0.5" />
              <span>Adds an extra layer of security to protect your funds</span>
            </li>
            <li className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#00d4aa] flex-shrink-0 mt-0.5" />
              <span>Required for withdrawals to prevent unauthorized transfers</span>
            </li>
            <li className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#00d4aa] flex-shrink-0 mt-0.5" />
              <span>Even if your password is compromised, your account remains secure</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
